/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { takeUntil } from 'rxjs';
import {
  BOOKING_TYPE,
  chaufferBookingStatusNum,
  PANEL_SERVICE_TYPE,
  RhRideStatus,
  rhEndedStatus,
  stageOneStatus,
  stageTwoDetailsStatus,
  values,
} from '../app/core/constants/keys.constant';
import { assets } from '../app/core/constants/assets.const';
import _ from 'underscore';

declare const google: any;

const RIDE_HAILING_ICONS = {
  pickup: assets.locationIcon,
  dropOff: assets.locationIcon,
  driver: assets.navigator,
};

const ROUTE_DEVIATION_THRESHOLD_METERS = 80;
const MIN_REROUTE_INTERVAL_MS = 15000; // 15s

const AUTO_FOLLOW_PAUSE_MS = 15000;

const ROUTE_FETCH_RETRY_DELAY_MS = 4000;
const PICKUP_DROPOFF_ICON_SIZE = 30;
const SHOW_COVERED_LIVE_PATH = false;

const MIN_BEARING_DISTANCE_METERS = 5;
const MIN_PAN_DISTANCE_METERS = 3;
const INITIAL_TRACKING_ZOOM = 15;

const MAPS_SDK_READY_RETRY_MS = 300;
const ROUTE_BOUNDS_PADDING = { top: 120, right: 60, bottom: 120, left: 60 };
const CONNECTOR_MIN_DISTANCE_METERS = 8; // ignore gaps too small to see

@Injectable({
  providedIn: 'root',
})
export class UserTrackingLogicService {
  private readonly pastBookingStatus: number[] = [
    values.RENTAL_STAGE_ONE_DROP_OFF_COMPLETED,
    values.RENTAL_TWO_DROP_OFF_COMPLETED,
    values.CHAUFFER_RIDE_STARTED,
  ];
  private static _callCount = 0;
  private DriverOverlayCtor: any = null;
  private LabeledMarkerOverlayCtor: any = null;

  getBookingTracking(ctx: any): void {
    if (!ctx.bookingId) return;
    // const isRideHailing = ctx.platformServiceType === PANEL_SERVICE_TYPE.RIDE_HAILING;
    // if (!isRideHailing && ctx.bookingDetail && ctx.bookingDetail.orderStatus && this.pastBookingStatus.includes(ctx.bookingDetail.orderStatus)) return;

    ctx._spinnerService.addToLoader('getTrackingDetail');
    ctx._bookingService.getBookingTracking(ctx.bookingId).subscribe({
      next: (res: any) => {
        ctx._spinnerService.removeFromLoader('getTrackingDetail');
        console.log('res', res);

        const normalizedDuration = res?.trip_duration ?? res?.distanceDetails ?? res?.last_duration ?? null;

        if (ctx.bookingDetail && normalizedDuration) {
          ctx.bookingDetail['last_duration'] = normalizedDuration;
          if (typeof ctx.updateTrackingEta === 'function') {
            ctx.updateTrackingEta();
          }
        }

        // if (isRideHailing) {
          // --- RIDE_HAILING ---
          if (!this.isRideHailingLive(ctx)) {
            this.clearRideHailingTracking(ctx);
            return;
          }

          const orderStatus = Number(ctx.bookingDetail?.orderStatus);

          if (this.shouldShowRideHailingSuggestedRoute(orderStatus)) {
            this.clearLiveRoutePolyline(ctx, '_rideHailingRouteCache', 'rideHailingLiveRoutePolyline');

            this.updateRideHailingDriverMarker(ctx, false, 0, 0);

            this.refreshRideHailingStageMarkers(ctx, false);
            this.updateRideHailingSuggestedRoute(ctx);

            return;
          }

          // Once the driver starts moving towards pickup, remove the planned Pickup -> Drop-off route.
          this.clearRideHailingSuggestedRoute(ctx);

          const headingToPickup = this.isRideHailingHeadingToPickup(ctx);
          this.refreshRideHailingStageMarkers(ctx, headingToPickup);

          const tracking = (res?.others ?? []).filter((item: any) => item?.latitude != null && item?.longitude != null).sort(
            (a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );

          if (!tracking.length) return;

          if (SHOW_COVERED_LIVE_PATH) this.setActualPathFromHistory(ctx, headingToPickup ? 'actualPathToPickup' : 'actualPathToDropOff', tracking);

          const latest = tracking[tracking.length - 1];
          const latitude = Number(latest.latitude);
          const longitude = Number(latest.longitude);

          if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            return;
          }

          this.applyDriverPosition(ctx, {
            // isRideHailing: true,
            headingToPickup,
            lat: latitude,
            lng: longitude,
            rawBearing: latest.bearing,
            panMap: true
          });
          return;
        // }

        // --- SELF_CHAUFFEUR ---
        if (ctx.bookingDetail?.bookingType == BOOKING_TYPE.SELF_DRIVING) {
          ctx.stageOneBookingTracking = res.stageOne.filter((item: any) => ctx.activeLiveTrackingStatus.includes(item.orderStatus));
          ctx.stageTwoBookingTracking = res.stageTwo.filter((item: any) => ctx.activeLiveTrackingStatus.includes(item.orderStatus));
        } else if (
          ctx.activeLiveTrackingStatus.includes(ctx.bookingDetail?.orderStatus)
        ) {
          ctx.stageOneBookingTracking = res.others.filter((item: any) => ctx.activeLiveTrackingStatus.includes(item.orderStatus));
        }

        if (SHOW_COVERED_LIVE_PATH) {
          this.setActualPathFromHistory(ctx, 'actualPathToPickup', ctx.stageOneBookingTracking);
          this.setActualPathFromHistory(ctx, 'actualPathToDropOff', ctx.stageTwoBookingTracking);
        }

        if (ctx.activeLiveTrackingStatus.includes(ctx.bookingDetail?.orderStatus)) {
          const headingToPickup = this.isHeadingToPickup(ctx);
          const historyPoints = headingToPickup ? ctx.stageOneBookingTracking : ctx.stageTwoBookingTracking;

          if (historyPoints && historyPoints.length) {
            const lastKnown = historyPoints[historyPoints.length - 1];
            const lat = Number(lastKnown.latitude);
            const lng = Number(lastKnown.longitude);

            if (Number.isFinite(lat) && Number.isFinite(lng)) {
              this.applyDriverPosition(ctx, {
                // isRideHailing: false,
                headingToPickup,
                lat,
                lng,
                rawBearing: lastKnown.bearing,
                panMap: true
              });
            }
          }
        }
      },
      error: () => ctx._spinnerService.removeFromLoader('getTrackingDetail')
    });
  }

  private isRideHailingLive(ctx: any): boolean {
    const status = ctx.bookingDetail?.orderStatus
    if (status === RhRideStatus.RIDE_COMPLETED) {
      return false;
    }
    return !!(ctx.bookingDetail && ctx.activeLiveTrackingStatus.includes(status));
  }

  private isRideHailingHeadingToPickup(ctx: any, orderStatus?: number): boolean {
    const status = Number(orderStatus ?? ctx.bookingDetail?.orderStatus);
    return this.shouldTrackDriverToPickup(status);
  }

  private shouldShowRideHailingPickupMarker(orderStatus: number | null | undefined): boolean {
    return [
      RhRideStatus.DRIVER_ASSIGNED,
      RhRideStatus.START_RIDE,
      RhRideStatus.NEAR_BY_PICKUP,
      RhRideStatus.ARRIVED_AT_PICKUP,
      RhRideStatus.WAITING,
    ].includes(Number(orderStatus) as any);
  }

  private shouldShowRideHailingDropOffMarker(orderStatus: number | null | undefined): boolean {
    return [
      RhRideStatus.DRIVER_ASSIGNED,
      RhRideStatus.RIDE_STARTED,
      RhRideStatus.NEAR_BY_ARRIVING,
      RhRideStatus.RIDE_COMPLETED,
      RhRideStatus.ARRIVED_AT_DROPOFF,
    ].includes(Number(orderStatus) as any);
  }

  /** True while a SELF_CHAUFFEUR booking is on the "heading to pickup" leg (self stage-one or chauffeur-en-route). Once false, the target is dropOff. */
  private isHeadingToPickup(ctx: any): boolean {
    return !!(ctx.bookingDetail?.orderStatus != null && (stageOneStatus.includes(ctx.bookingDetail.orderStatus) || chaufferBookingStatusNum.includes(ctx.bookingDetail.orderStatus)));
  }

  /** Recenters the map on the user's current browser location. Used once a ride-hailing trip ends. */
  private recenterMapToCurrentUserLocation(ctx: any): void {
    if (!ctx.map || typeof navigator === 'undefined' || !navigator.geolocation)
      return;

    navigator.geolocation.getCurrentPosition(
      (position: GeolocationPosition) => {
        const currentLatLng = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);
        ctx.map.setCenter(currentLatLng);
        ctx.map.setZoom(14);
      },
      (error: GeolocationPositionError) => {
        console.error('Unable to fetch current location for recenter', error);
      },
    );
  }

  // ============================================================
  // Shared live-route caching (RIDE_HAILING + SELF_CHAUFFEUR)
  // ============================================================
  private findNearestRouteIndex(point: { lat: number; lng: number }, route: { lat: number; lng: number }[]): { index: number; distance: number } {
    let bestIndex = 0;
    let bestDistance = Infinity;
    for (let i = 0; i < route.length; i++) {
      const distance = this.getDistanceBetweenPoints(
        new google.maps.LatLng(point.lat, point.lng),
        new google.maps.LatLng(route[i].lat, route[i].lng),
      );
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = i;
      }
    }
    return { index: bestIndex, distance: bestDistance };
  }

  private computeBearing(p1: { lat: number; lng: number }, p2: { lat: number; lng: number }): number {
    const toRad = (d: number) => (d * Math.PI) / 180;
    const toDeg = (r: number) => (r * 180) / Math.PI;
    const lat1 = toRad(p1.lat);
    const lat2 = toRad(p2.lat);
    const dLng = toRad(p2.lng - p1.lng);
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
    return (toDeg(Math.atan2(y, x)) + 360) % 360;
  }

  private snapToRoute(cache: any, point: { lat: number; lng: number }, fallbackBearing?: number): { lat: number; lng: number; bearing: number } {
    const fallback = {
      lat: point.lat,
      lng: point.lng,
      bearing: Number.isFinite(Number(fallbackBearing)) ? Number(fallbackBearing) : 0,
    };

    if (!cache?.coords?.length) return fallback;

    const { index, distance } = this.findNearestRouteIndex(point, cache.coords);
    if (distance > ROUTE_DEVIATION_THRESHOLD_METERS) return fallback;

    const snapped = cache.coords[index];
    const next = cache.coords[Math.min(index + 1, cache.coords.length - 1)];
    const bearing = snapped.lat === next.lat && snapped.lng === next.lng ? fallback.bearing : this.computeBearing(snapped, next);

    return { lat: snapped.lat, lng: snapped.lng, bearing };
  }

  private fitRouteBounds(ctx: any,points: { lat: number; lng: number }[],padding: { top: number; right: number; bottom: number; left: number } = ROUTE_BOUNDS_PADDING,): void {
    if (!ctx.map || !points?.length) return;

    const bounds = new google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(new google.maps.LatLng(p.lat, p.lng)));

    try {
      ctx.map.fitBounds(bounds, padding);
    } catch {
      // Older Maps JS versions don't accept a padding object — fall back to a plain fit.
      ctx.map.fitBounds(bounds);
    }

    ctx._initialTrackingViewportSet = true;
  }

  private fetchAndCacheRoute(ctx: any, cacheKey: string, polylineKey: string, legKey: string, originLat: number, originLng: number, targetLat: number, targetLng: number): void {
    const existing = ctx[cacheKey];
    if (existing?.fetching) return;

    // TEMP: verify quota-safe behavior — count real DirectionsService.route() calls.
    console.log(`[DirectionsService] route() call #${(UserTrackingLogicService._callCount = (UserTrackingLogicService._callCount || 0) + 1)}`,
      {
        cacheKey,
        legKey,
        bookingId: ctx.bookingId,
        origin: { lat: originLat, lng: originLng },
        target: { lat: targetLat, lng: targetLng },
      },
    );

    const placeholderCoords = existing?.leg === legKey && existing.coords?.length
        ? existing.coords
        : [
            { lat: originLat, lng: originLng },
            { lat: targetLat, lng: targetLng },
          ];

    ctx[cacheKey] = {
      coords: placeholderCoords,
      leg: legKey,
      fetching: true,
      lastFetchAt: Date.now(),
    };

    if (ctx[polylineKey] && (!existing || existing.leg !== legKey || !existing.coords?.length)) {
      ctx[polylineKey].setPath(placeholderCoords.map((p: any) => new google.maps.LatLng(p.lat, p.lng)),);
    }

    if (!existing || existing.leg !== legKey) {
      this.clearConnector(ctx, 'liveTargetConnectorPolyline');
    }

    if (!ctx.directionsService) {
      ctx.directionsService = new google.maps.DirectionsService();
    }

    const origin = new google.maps.LatLng(originLat, originLng);
    const destination = new google.maps.LatLng(targetLat, targetLng);

    ctx.directionsService.route(
      {
        origin,
        destination,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (response: any, status: any) => {
        const cache = ctx[cacheKey];
        if (cache) cache.fetching = false;

        if (status !== google.maps.DirectionsStatus.OK || !response?.routes?.length) {
          console.log('[DirectionsService] route() FAILED — will retry', {
            cacheKey,
            legKey,
            status,
          });
          setTimeout(() => {
            const current = ctx[cacheKey];
            // Only retry if nothing else has since taken over this cache slot (leg change,
            // or a fetch that started/succeeded in the meantime).
            if (current?.leg === legKey && !current.fetching) {
              this.fetchAndCacheRoute(
                ctx,
                cacheKey,
                polylineKey,
                legKey,
                originLat,
                originLng,
                targetLat,
                targetLng,
              );
            }
          }, ROUTE_FETCH_RETRY_DELAY_MS);
          return;
        }

        const overviewPath = response.routes[0].overview_path;
        const coords = overviewPath.map((p: any) => ({
          lat: p.lat(),
          lng: p.lng(),
        }));

        ctx[cacheKey] = {
          coords,
          leg: legKey,
          fetching: false,
          lastFetchAt: Date.now(),
        };

        if (ctx[polylineKey]) {
          ctx[polylineKey].setPath(overviewPath);
        }

        this.renderConnector(ctx, 'liveTargetConnectorPolyline',
        { lat: targetLat, lng: targetLng }, coords[coords.length - 1]);

        if (ctx._routeBoundsFittedForLeg !== legKey) {
          ctx._routeBoundsFittedForLeg = legKey;
          this.fitRouteBounds(ctx, coords);
        }
      },
    );
  }

  private updateLiveRoutePolyline(
    ctx: any,
    cacheKey: string,
    polylineKey: string,
    legKey: string,
    driverLat: number,
    driverLng: number,
    targetLat: number,
    targetLng: number,
    color: string,
    dotted: boolean,
  ): void {
    if (!ctx[polylineKey]) {
      ctx[polylineKey] = new google.maps.Polyline({
        map: ctx.map,
        geodesic: true,
        strokeWeight: 3.5,
        zIndex: 5,
        strokeColor: color,
      });
    } else {
      if (!ctx[polylineKey].getMap()) ctx[polylineKey].setMap(ctx.map);
    }

    const cache = ctx[cacheKey];
    const driverPoint = { lat: driverLat, lng: driverLng };

    // No cached route yet, or the target leg changed (pickup -> dropOff) — must fetch.
    // fetchAndCacheRoute immediately seeds a straight-line placeholder onto the polyline
    // we just created above, so it's never left blank (Fix #3).
    if (!cache || cache.leg !== legKey || !cache.coords.length) {
      this.fetchAndCacheRoute(
        ctx,
        cacheKey,
        polylineKey,
        legKey,
        driverLat,
        driverLng,
        targetLat,
        targetLng,
      );
      return;
    }

    const { index, distance } = this.findNearestRouteIndex(
      driverPoint,
      cache.coords,
    );

    if (distance > ROUTE_DEVIATION_THRESHOLD_METERS) {
      // Driver actually left the cached route — a real reroute is warranted, but
      // still throttled so one noisy GPS fix can't trigger a request storm.
      if (!cache.fetching && Date.now() - (cache.lastFetchAt || 0) >= MIN_REROUTE_INTERVAL_MS) {
        this.fetchAndCacheRoute(
          ctx,
          cacheKey,
          polylineKey,
          legKey,
          driverLat,
          driverLng,
          targetLat,
          targetLng,
        );
      }
      return;
    }

    // Still on the cached route: trim it locally (zero API cost) so the line
    // visually shortens as the driver progresses.
    console.log(`[DirectionsService] local trim (no API call) — leg: ${legKey}, cacheKey: ${cacheKey}`,);
    const nextIndex = Math.min(index + 1, cache.coords.length - 1);
    const remainingTail = cache.coords.slice(nextIndex);
    const trimmed = [{ lat: driverLat, lng: driverLng }, ...remainingTail];

    cache.coords = trimmed;
    ctx[polylineKey].setPath(
     trimmed.map((p: any) => new google.maps.LatLng(p.lat, p.lng))
    );
  }

  private applyDriverPosition(
    ctx: any,
    params: {
      // isRideHailing: boolean;
      headingToPickup: boolean;
      lat: number;
      lng: number;
      rawBearing?: number;
      panMap?: boolean;
    },
  ): void {
    if (!ctx.map) return;

  const { headingToPickup, lat, lng, rawBearing, panMap } = params;
  const cacheKey = '_rideHailingRouteCache';
  const polylineKey = 'rideHailingLiveRoutePolyline';
  const target = headingToPickup ? ctx.bookingDetail?.pickup : ctx.bookingDetail?.dropOf;

    // if (!isRideHailing && this.isAtStopOrWaiting(ctx)) {
    //   this.clearLiveRoutePolyline(ctx, cacheKey, polylineKey);
    //   this.updateChaufferDriverMarker(ctx, lat, lng, rawBearing);
    //   if (SHOW_COVERED_LIVE_PATH) this.appendActualPathPoint(ctx, headingToPickup, lat, lng);
    //   if (panMap && ctx.map) {
    //     if (!this.focusInitialTrackingViewport(ctx, lat, lng) && this.shouldPanTo(ctx, lat, lng)) {
    //       ctx.map.panTo(new google.maps.LatLng(lat, lng));
    //     }
    //   }
    //   return;
    // }

    if (this.isAtStopOrWaiting(ctx)) {
      this.clearLiveRoutePolyline(ctx, cacheKey, polylineKey);
      this.updateRideHailingDriverMarker(ctx, true, lat, lng, rawBearing);
      if (SHOW_COVERED_LIVE_PATH) this.appendActualPathPoint(ctx, headingToPickup, lat, lng);
      if (panMap && ctx.map) {
        if (!this.focusInitialTrackingViewport(ctx, lat, lng) && this.shouldPanTo(ctx, lat, lng)) {
          ctx.map.panTo(new google.maps.LatLng(lat, lng));
        }
      }
      return;
    }

    if (target?.latitude != null && target?.longitude != null) {
      const targetLat = Number(target.latitude);
      const targetLng = Number(target.longitude);
      if (Number.isFinite(targetLat) && Number.isFinite(targetLng)) {
        this.updateLiveRoutePolyline(
          ctx,
          cacheKey,
          polylineKey,
          headingToPickup ? 'pickup' : 'dropOff',
          lat,
          lng,
          targetLat,
          targetLng,
          '#000000',
          headingToPickup,
        );
      }
    }

    const snapped = this.snapToRoute(ctx[cacheKey], { lat, lng }, rawBearing);

    // if (isRideHailing) {
      this.updateRideHailingDriverMarker(ctx, true, snapped.lat, snapped.lng, rawBearing);
    // } else {
    //   this.updateChaufferDriverMarker(ctx, snapped.lat, snapped.lng, rawBearing);
    // }

    if (SHOW_COVERED_LIVE_PATH) this.appendActualPathPoint(ctx, headingToPickup, lat, lng);

    if (panMap && ctx.map && this.shouldPanTo(ctx, snapped.lat, snapped.lng)) {
      const driverLatLng = new google.maps.LatLng(snapped.lat, snapped.lng);
      if (!ctx._initialTrackingViewportSet) {
        ctx.map.setCenter(driverLatLng);
        ctx.map.setZoom(INITIAL_TRACKING_ZOOM);

        ctx._initialTrackingViewportSet = true;
        ctx._lastPannedPosition = {
          lat: snapped.lat,
          lng: snapped.lng,
        };

        return;
      }

      ctx.map.panTo(driverLatLng);
    }
  }

  // ============================================================
  // Actual path driven (blue breadcrumb)
  // ============================================================

  private appendActualPathPoint(
    ctx: any,
    headingToPickup: boolean,
    lat: number,
    lng: number,
  ): void {
    const bucketKey = headingToPickup ? 'actualPathToPickup' : 'actualPathToDropOff';
    const bucket: { lat: number; lng: number }[] = ctx[bucketKey] || (ctx[bucketKey] = []);
    const last = bucket[bucket.length - 1];
    if (last && last.lat === lat && last.lng === lng) return;
    bucket.push({ lat, lng });
    this.renderActualPathPolyline(ctx, bucketKey);
  }

  private isAtStopOrWaiting(ctx: any): boolean {
    const status = ctx.bookingDetail?.orderStatus;
    if (status == null) return false;

    if (ctx.platformServiceType === PANEL_SERVICE_TYPE.RIDE_HAILING) {
      return false;
    }

    return (
      status === values.CHAUFFER_NEAR_BY_LOCATION ||
      status === values.CHAUFFER_ARRIVE_NOW ||
      status === values.RENTAL_STAGE_ONE_NEAR_BY_LOCATION ||
      status === values.RENTAL_STAGE_TWO_NEAR_BY_LOCATION
    );
  }

  private setActualPathFromHistory(
    ctx: any,
    bucketKey: 'actualPathToPickup' | 'actualPathToDropOff',
    points: any[],
  ): void {
    const coords = (points || []).map((p: any) => ({ lat: Number(p.latitude), lng: Number(p.longitude) })).filter((p: any) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    if (!coords.length) return;
    ctx[bucketKey] = coords;
    this.renderActualPathPolyline(ctx, bucketKey);
  }

  /** Blue actual-path line: dotted for the pickup leg, solid for the dropOff leg. */
  private renderActualPathPolyline(ctx: any, bucketKey: 'actualPathToPickup' | 'actualPathToDropOff'): void {
    const coords: { lat: number; lng: number }[] = ctx[bucketKey];
    if (!coords || coords.length < 2) return;

    const polylineKey = bucketKey + 'Polyline';
    const path = coords.map((p) => new google.maps.LatLng(p.lat, p.lng));

    if (!ctx[polylineKey]) {
      ctx[polylineKey] = new google.maps.Polyline({
        map: ctx.map,
        path,
        geodesic: true,
        strokeWeight: 3.5,
        zIndex: 6,
        strokeColor: '#000000',
      });
    } else {
      ctx[polylineKey].setPath(path);
      if (!ctx[polylineKey].getMap()) ctx[polylineKey].setMap(ctx.map);
    }
  }

  private focusInitialTrackingViewport(ctx: any, lat: number, lng: number): boolean {
    if (!ctx.map || ctx._initialTrackingViewportSet) return false;

    google.maps.event.trigger(ctx.map, 'resize');
    
    const position = new google.maps.LatLng(lat, lng);
    ctx.map.setCenter(position);
    ctx.map.setZoom(INITIAL_TRACKING_ZOOM);

    ctx._initialTrackingViewportSet = true;
    ctx._lastPannedPosition = { lat, lng };

    return true;
  }

  initializeMapAndRoute(ctx: any): void {
    if (!ctx.bookingDetail) return;
    if (typeof google === 'undefined') {
      console.warn('[UserTrackingLogicService] Google Maps API not ready yet — retrying.');
      const retry = () => this.initializeMapAndRoute(ctx);
      setTimeout(() => {
        if (ctx._zone && typeof ctx._zone.run === 'function') {
          ctx._zone.run(retry);
        } else {
          retry();
        }
      }, MAPS_SDK_READY_RETRY_MS);
      return;
    }

    const mapRef = document.getElementById('map') as HTMLElement | null;
    if (!mapRef) {
      const retry = () => this.initializeMapAndRoute(ctx);
      setTimeout(() => {
        if (ctx._zone && typeof ctx._zone.run === 'function') {
          ctx._zone.run(retry);
        } else {
          retry();
        }
      }, MAPS_SDK_READY_RETRY_MS);
      return;
    }

    if (ctx.driverMarker) {
      ctx.driverMarker.setMap(null);
      ctx.driverMarker = null;
    }
    if (ctx.pickupMarker) {
      ctx.pickupMarker.setMap(null);
      ctx.pickupMarker = null;
    }
    if (ctx.dropOffMarker) {
      ctx.dropOffMarker.setMap(null);
      ctx.dropOffMarker = null;
    }
    if (ctx.vendorMarker) {
      ctx.vendorMarker.setMap(null);
      ctx.vendorMarker = null;
    }

    this.disposeRouteOverlays(ctx);

    ctx._rideHailingRouteCache = undefined;
    ctx._rideHailingSuggestedRouteCache = undefined;
    ctx._chaufferRouteCache = undefined;
    ctx.rideHailingLiveRoutePolyline = undefined;
    ctx.chaufferLiveRoutePolyline = undefined;
    ctx.suggestedTripRoutePolyline = undefined;
    ctx.actualPathToPickup = undefined;
    ctx.actualPathToDropOff = undefined;
    ctx.actualPathToPickupPolyline = undefined;
    ctx.actualPathToDropOffPolyline = undefined;
    ctx._rideHailingEndedHandled = false;
    ctx._markerAnimationFrames = undefined;
    ctx._rideHailingBearingState = undefined;
    ctx._chaufferBearingState = undefined;
    ctx._lastPannedPosition = undefined;
    ctx._initialTrackingViewportSet = false;
    ctx._routeBoundsFittedForLeg = undefined;

    // const isRideHailing = ctx.platformServiceType === PANEL_SERVICE_TYPE.RIDE_HAILING;

    const pickupLat = ctx.bookingDetail.pickup ? ctx.bookingDetail.pickup.latitude : '';
    const pickupLng = ctx.bookingDetail.pickup ? ctx.bookingDetail.pickup.longitude : '';
    // const vendorLat = !isRideHailing && ctx.bookingDetail.carId && ctx.bookingDetail.carId.garageAddress ? ctx.bookingDetail.carId.garageAddress.latitude : '';
    // const vendorLng = !isRideHailing && ctx.bookingDetail.carId && ctx.bookingDetail.carId.garageAddress ? ctx.bookingDetail.carId.garageAddress.longitude : '';
    const dropOffLat = ctx.bookingDetail.dropOf ? ctx.bookingDetail.dropOf.latitude : '';
    const dropOffLng = ctx.bookingDetail.dropOf ? ctx.bookingDetail.dropOf.longitude : '';

    if (!pickupLat || !pickupLng || !dropOffLat || !dropOffLng) {
      console.error('Invalid latitude or longitude values:', {
        pickupLat,
        pickupLng,
        // vendorLat,
        // vendorLng,
        dropOffLat,
        dropOffLng
      });
      return;
    }

    const center = new google.maps.LatLng(pickupLat, pickupLng);
    const finishSetup = () => {
    ctx.directionsService = new google.maps.DirectionsService();
    ctx.directionsRenderer = new google.maps.DirectionsRenderer({
      polylineOptions: {
        strokeColor: '#000000',
        strokeOpacity: 1.0,
        strokeWeight: 3.5
      },
      suppressMarkers: true,
      preserveViewport: true,
    });
    ctx.directionsRenderer.setMap(ctx.map);

    // if (isRideHailing) {
      if (!this.isRideHailingLive(ctx)) {
        this.clearRideHailingTracking(ctx);
        return;
      }

      const orderStatus = Number(ctx.bookingDetail?.orderStatus);

      this.refreshRideHailingMarkers(ctx);

      if (this.shouldShowRideHailingSuggestedRoute(orderStatus)) {
        this.updateRideHailingSuggestedRoute(ctx);
      } else {
        this.clearRideHailingSuggestedRoute(ctx);
      }

      ctx.getBookingTracking();

      return;
    // }

    // --- SELF_CHAUFFEUR ---
    if (!ctx.activeLiveTrackingStatus.includes(ctx.bookingDetail.orderStatus)) {
      return;
    }

    if (stageOneStatus.includes(ctx.bookingDetail.orderStatus) || chaufferBookingStatusNum.includes(ctx.bookingDetail.orderStatus)) {
      this.stageOneTracking(ctx, {
        vendor: ctx.bookingDetail.carId && ctx.bookingDetail.carId.garageAddress ? ctx.bookingDetail.carId.garageAddress : {},
        pickup: ctx.bookingDetail.pickup ? ctx.bookingDetail.pickup : {},
        dropOff: ctx.bookingDetail.dropOf ? ctx.bookingDetail.dropOf : {},
      });
    } else if (stageTwoDetailsStatus.includes(ctx.bookingDetail.orderStatus)) {
      this.stageTwoTracking(ctx, {
        dropOff: ctx.bookingDetail.dropOf ? ctx.bookingDetail.dropOf : {},
        pickup: ctx.bookingDetail.pickup ? ctx.bookingDetail.pickup : {},
      });
    }
    ctx.getBookingTracking();
    };

    if (!ctx.map) {
      ctx.map = new google.maps.Map(mapRef, {
        zoom: INITIAL_TRACKING_ZOOM,
        center,
        mapTypeControl: false,
        streetViewControl: false,
        scaleControl: true,
        indoorLevelPicker: false,
        clickableIcons: false,
        gestureHandling: 'greedy',
        scrollwheel: true,
        styles: [
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
          { featureType: 'administrative.locality', elementType: 'labels.text', stylers: [{ visibility: 'on' }] },
          { featureType: 'administrative.neighborhood', elementType: 'labels.text', stylers: [{ visibility: 'on' }] },
          { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
          { featureType: 'road', elementType: 'labels.text', stylers: [{ visibility: 'on' }] },
        ],
      });

      const pauseAutoFollow = () => { ctx._autoFollowPausedUntil = Date.now() + AUTO_FOLLOW_PAUSE_MS; };
      ctx.map.addListener('dragstart', pauseAutoFollow);
      mapRef.addEventListener('wheel', pauseAutoFollow, { passive: true });
      mapRef.addEventListener('touchstart', pauseAutoFollow, { passive: true });

      requestAnimationFrame(() => {
        google.maps.event.trigger(ctx.map, 'resize');
        ctx.map.setCenter(center);
        finishSetup();
      });
    } else {
      ctx.map.setCenter(center);
      google.maps.event.trigger(ctx.map, 'resize');
      finishSetup();
    }
  }

  private stageOneTracking(
    ctx: any,
    obj: any,
    forceFullRoute: boolean = false,
    icons: {
      pickup: string;
      dropOff: string;
      pickupSize?: number;
      dropOffSize?: number;
    } = { pickup: assets.pickupMarker, dropOff: assets.dropoffMarker }
  ): void {
    ctx.trackingStage = 1;
    const pickLng = obj.pickup.longitude;
    const drawFullRoute = forceFullRoute || chaufferBookingStatusNum.includes(ctx.bookingDetail?.orderStatus);
    const pickupSize = icons.pickupSize ?? PICKUP_DROPOFF_ICON_SIZE;
    const dropOffSize = icons.dropOffSize ?? PICKUP_DROPOFF_ICON_SIZE;

    ctx.pickupMarker = this.renderLabeledMarker(ctx, 'pickupMarker', icons.pickup, 'Pickup', obj.pickup.latitude, pickLng, pickupSize);

    if (drawFullRoute && obj.dropOff?.latitude != null && obj.dropOff?.longitude != null) {
      ctx.dropOffMarker = this.renderLabeledMarker(ctx, 'dropOffMarker', icons.dropOff, 'Drop-off', obj.dropOff.latitude, obj.dropOff.longitude, dropOffSize);
    }
  }

  private getDistanceBetweenPoints(p1: google.maps.LatLng, p2: google.maps.LatLng): number {
    const R = 6371000;
    const lat1 = (p1.lat() * Math.PI) / 180;
    const lat2 = (p2.lat() * Math.PI) / 180;
    const dLat = lat2 - lat1;
    const dLon = ((p2.lng() - p1.lng()) * Math.PI) / 180;

    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  stageTwoTracking(ctx: any, obj: any): void {
    ctx.trackingStage = 2;
    ctx.dropOffMarker = this.renderLabeledMarker(
      ctx,
      'dropOffMarker',
      assets.dropoffMarker,
      'Drop-off',
      obj.dropOff.latitude,
      obj.dropOff.longitude,
      PICKUP_DROPOFF_ICON_SIZE,
    );
  }

  private defineLabeledMarkerOverlayClass(): void {
    if (this.LabeledMarkerOverlayCtor) return;
    this.LabeledMarkerOverlayCtor = class extends google.maps.OverlayView {
      private div: HTMLDivElement | null = null;
      private img: HTMLImageElement | null = null;
      private position: any;
      private iconUrl: string;
      private size: number;
      private label: string;

      constructor(position: { lat: number; lng: number }, iconUrl: string, label: string, map: any, size: number) {
        super();
        this.position = new google.maps.LatLng(position.lat, position.lng);
        this.iconUrl = iconUrl;
        this.label = label;
        this.size = size;
        (this as any).setMap(map);
      }

      onAdd(): void {
        this.div = document.createElement('div');
        this.div.style.position = 'absolute';
        this.div.style.display = 'flex';
        this.div.style.flexDirection = 'column';
        this.div.style.alignItems = 'center';
        this.div.style.pointerEvents = 'none';

        const labelEl = document.createElement('div');
        labelEl.textContent = this.label;
        labelEl.style.background = '#101828';
        labelEl.style.color = '#ffffff';
        labelEl.style.fontSize = '12px';
        labelEl.style.fontWeight = '600';
        labelEl.style.padding = '4px 10px';
        labelEl.style.borderRadius = '14px';
        labelEl.style.whiteSpace = 'nowrap';
        labelEl.style.marginBottom = '4px';
        labelEl.style.boxShadow = '0 1px 4px rgba(16,24,40,0.25)';
        this.div.appendChild(labelEl);

        this.img = document.createElement('img');
        this.img.src = this.iconUrl;
        this.img.style.width = `${this.size}px`;
        this.img.style.height = `${this.size}px`;
        this.img.style.display = 'block';
        this.div.appendChild(this.img);

        (this as any).getPanes()?.overlayMouseTarget.appendChild(this.div);
      }

      draw(): void {
        if (!this.div) return;
        const projection = (this as any).getProjection();
        if (!projection) return;
        const point = projection.fromLatLngToDivPixel(this.position);
        if (!point) return;
        const half = this.size / 2;
        const totalHeight = this.div.offsetHeight || this.size;
        this.div.style.left = `${point.x - half}px`;
        this.div.style.top = `${point.y - totalHeight}px`; // anchor pin tip to the coordinate
      }

      onRemove(): void {
        this.div?.remove();
        this.div = null;
        this.img = null;
      }

      setPosition(position: { lat: number; lng: number }): void {
        this.position = new google.maps.LatLng(position.lat, position.lng);
        this.draw();
      }

      setIcon(icon: { url: string; scaledSize?: { width: number; height: number } }): void {
        this.iconUrl = icon.url;
        if (icon.scaledSize?.width) this.size = icon.scaledSize.width;
        if (this.img) {
          this.img.src = this.iconUrl;
          this.img.style.width = `${this.size}px`;
          this.img.style.height = `${this.size}px`;
        }
        this.draw();
      }
    };
  }

  private renderLabeledMarker(ctx: any,overlayKey: string,iconUrl: string,label: string,lat: number,lng: number,size: number = PICKUP_DROPOFF_ICON_SIZE): any {
    this.defineLabeledMarkerOverlayClass();
    const position = { lat, lng };

    if (ctx[overlayKey]) {
      ctx[overlayKey].setMap(ctx.map);
      ctx[overlayKey].setPosition(position);
      ctx[overlayKey].setIcon({ url: iconUrl, scaledSize: { width: size, height: size } });
      return ctx[overlayKey];
    }

    ctx[overlayKey] = new this.LabeledMarkerOverlayCtor(position, iconUrl, label, ctx.map, size);
    return ctx[overlayKey];
  }

  // ============================================================
  // RIDE_HAILING driver tracking
  // ============================================================
  private updateRideHailingDriverMarker(
    ctx: any,
    showMarker: boolean,
    latitude: number,
    longitude: number,
    bearing?: number,
  ): void {
    if (!showMarker) {
      if (ctx.driverMarker) {
        ctx.driverMarker.setMap(null);
        ctx.driverMarker = null;
      }
      return;
    }

    const resolvedBearing = this.resolveDriverBearing(
      ctx,
      '_rideHailingBearingState',
      latitude,
      longitude,
      bearing,
    );

    this.renderDriverOverlay(
      ctx,
      'driverMarker',
      RIDE_HAILING_ICONS.driver,
      latitude,
      longitude,
      resolvedBearing,
      44,
    );
  }

  refreshRideHailingMarkers(ctx: any): void {
    if (!ctx.bookingDetail || !ctx.map) return;

    const orderStatus = Number(ctx.bookingDetail.orderStatus);
    const showPickup = this.shouldShowRideHailingPickupMarker(orderStatus);
    const showDropOff = this.shouldShowRideHailingDropOffMarker(orderStatus);
    const pickup = ctx.bookingDetail.pickup;
    const dropOff = ctx.bookingDetail.dropOf;

    if (showPickup && pickup?.latitude != null && pickup?.longitude != null) {
      this.renderLabeledMarker(ctx, 'pickupMarker', RIDE_HAILING_ICONS.pickup, 'Pickup', Number(pickup.latitude), Number(pickup.longitude), PICKUP_DROPOFF_ICON_SIZE);
    } else if (ctx.pickupMarker) {
      ctx.pickupMarker.setMap(null);
    }

    if (showDropOff && dropOff?.latitude != null && dropOff?.longitude != null) {
      this.renderLabeledMarker(ctx, 'dropOffMarker', RIDE_HAILING_ICONS.dropOff, 'Drop-off', Number(dropOff.latitude), Number(dropOff.longitude), PICKUP_DROPOFF_ICON_SIZE);
    } else if (ctx.dropOffMarker) {
      ctx.dropOffMarker.setMap(null);
    }

    if (!this.shouldShowRideHailingSuggestedRoute(orderStatus)) {
      this.clearRideHailingSuggestedRoute(ctx);
    }
  }

  private refreshRideHailingStageMarkers(ctx: any, headingToPickup: boolean): void {
    ctx._rideHailingHeadingToPickup = headingToPickup;
    this.refreshRideHailingMarkers(ctx);
  }

  // ==================================================================
  // SELF_CHAUFFEUR driver tracking
  // ==================================================================
  private updateChaufferDriverMarker(
    ctx: any,
    latitude: number,
    longitude: number,
    bearing?: number,
  ): void {
    const resolvedBearing = this.resolveDriverBearing(
      ctx,
      '_chaufferBearingState',
      latitude,
      longitude,
      bearing,
    );

    this.renderDriverOverlay(
      ctx,
      'driverMarker',
      assets.navigator,
      latitude,
      longitude,
      resolvedBearing,
      44,
    );
  }

  // ============================================================
  // Shared driver-overlay + bearing helpers (Fix: bearing / front-face)
  // ============================================================

  private defineDriverOverlayClass(): void {
    if (this.DriverOverlayCtor) return;
    this.DriverOverlayCtor = class extends google.maps.OverlayView {
      private div: HTMLDivElement | null = null;
      private position: any;
      private rotation = 0;
      private readonly iconUrl: string;
      private readonly size: number;

      constructor(position: { lat: number; lng: number }, iconUrl: string, map: any, size: number) {
        super();
        this.position = new google.maps.LatLng(position.lat, position.lng);
        this.iconUrl = iconUrl;
        this.size = size;
        (this as any).setMap(map);
      }

      onAdd(): void {
        this.div = document.createElement('div');
        this.div.style.position = 'absolute';
        this.div.style.width = `${this.size}px`;
        this.div.style.height = `${this.size}px`;
        this.div.style.pointerEvents = 'none';

        const img = document.createElement('img');
        img.src = this.iconUrl;
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.display = 'block';
        img.style.transformOrigin = '50% 50%';
        img.style.transition = 'transform 0.3s ease';
        img.style.transform = `rotate(${this.rotation}deg)`;
        this.div.appendChild(img);

        (this as any).getPanes()?.overlayMouseTarget.appendChild(this.div);
      }

      draw(): void {
        if (!this.div) return;
        const projection = (this as any).getProjection();
        if (!projection) return;
        const point = projection.fromLatLngToDivPixel(this.position);
        if (!point) return;

        const half = this.size / 2;
        this.div.style.left = `${point.x - half}px`;
        this.div.style.top = `${point.y - half}px`;
      }

      onRemove(): void {
        this.div?.remove();
        this.div = null;
      }

      setPosition(position: { lat: number; lng: number }): void {
        this.position = new google.maps.LatLng(position.lat, position.lng);
        this.draw();
      }

      setRotation(rotation: number): void {
        this.rotation = rotation;
        const img = this.div?.querySelector('img') as HTMLImageElement | null;
        if (img) img.style.transform = `rotate(${rotation}deg)`;
      }
    };
  }

  private renderDriverOverlay(
    ctx: any,
    overlayKey: string,
    iconUrl: string,
    lat: number,
    lng: number,
    bearing: number,
    size: number,
  ): void {
    this.defineDriverOverlayClass();
    const position = { lat, lng };

    if (ctx[overlayKey]) {
      ctx[overlayKey].setPosition(position);
      ctx[overlayKey].setRotation(bearing);
      return;
    }

    ctx[overlayKey] = new this.DriverOverlayCtor(position, iconUrl, ctx.map, size);
    ctx[overlayKey].setRotation(bearing);
  }

  private resolveDriverBearing(
    ctx: any,
    bearingStateKey: string,
    lat: number,
    lng: number,
    rawBearing?: number,
  ): number {
    const state = ctx[bearingStateKey] || (ctx[bearingStateKey] = { lastPosition: null, lastBearing: 0 });

    let bearing: number | null = Number.isFinite(Number(rawBearing)) ? Number(rawBearing) : null;

    if (bearing === null && state.lastPosition) {
      const moved = this.getDistanceBetweenPoints(
        new google.maps.LatLng(state.lastPosition.lat, state.lastPosition.lng),
        new google.maps.LatLng(lat, lng),
      );
      bearing = moved > MIN_BEARING_DISTANCE_METERS ? this.computeBearing(state.lastPosition, { lat, lng }) : state.lastBearing;
    }
    if (bearing === null) bearing = state.lastBearing ?? 0;

    state.lastBearing = bearing;
    state.lastPosition = { lat, lng };
    return bearing ?? 0;
  }

  private shouldPanTo(ctx: any, lat: number, lng: number): boolean {
    if (this.isAutoFollowPaused(ctx)) return false;

    const last = ctx._lastPannedPosition;
    if (!last) {
      ctx._lastPannedPosition = { lat, lng };
      return true;
    }
    const moved = this.getDistanceBetweenPoints(
      new google.maps.LatLng(last.lat, last.lng),
      new google.maps.LatLng(lat, lng),
    );
    if (moved < MIN_PAN_DISTANCE_METERS) return false;
    ctx._lastPannedPosition = { lat, lng };
    return true;
  }

  // ============================================================
  // Dotted connector: marker coordinate -> snapped route end
  // ============================================================
  private renderConnector(
    ctx: any,
    key: string,
    from: { lat: number; lng: number } | null,
    to: { lat: number; lng: number } | null,
  ): void {
    if (!ctx.map || !from || !to || !Number.isFinite(from.lat) || !Number.isFinite(from.lng)) {
      this.clearConnector(ctx, key);
      return;
    }

    const gap = this.getDistanceBetweenPoints(
      new google.maps.LatLng(from.lat, from.lng),
      new google.maps.LatLng(to.lat, to.lng),
    );
    if (gap < CONNECTOR_MIN_DISTANCE_METERS) {
      this.clearConnector(ctx, key);
      return;
    }

    const path = [
      new google.maps.LatLng(from.lat, from.lng),
      new google.maps.LatLng(to.lat, to.lng),
    ];

    if (!ctx[key]) {
      ctx[key] = new google.maps.Polyline({
        map: ctx.map,
        path,
        geodesic: false,
        strokeOpacity: 0, // the visible dots come from the repeated icon below
        zIndex: 5,
        clickable: false,
        icons: [
          {
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: '#000000',
              fillOpacity: 1,
              strokeColor: '#000000',
              strokeOpacity: 1,
              scale: 1.8,
            },
            offset: '0',
            repeat: '8px',
          },
        ],
      });
    } else {
      ctx[key].setPath(path);
      if (!ctx[key].getMap()) ctx[key].setMap(ctx.map);
    }
  }

  private clearConnector(ctx: any, key: string): void {
    if (ctx[key]) {
      ctx[key].setMap(null);
      ctx[key] = null;
    }
  }

  private clearAllConnectors(ctx: any): void {
    [
      'suggestedPickupConnectorPolyline',
      'suggestedDropOffConnectorPolyline',
      'liveTargetConnectorPolyline',
    ].forEach((key) => this.clearConnector(ctx, key));
  }

  private addMarkerToMap(
    ctx: any,
    location: any,
    markerIcon: any,
    InfoWindow: string = '',
    isDraggable: boolean = false,
  ): any {
    const marker = new google.maps.Marker({
      position: {
        lat: location.lat,
        lng: location.lng,
      },
      map: ctx.map,
      draggable: isDraggable,
      icon: markerIcon,
      title: location.type,
    });
    if (InfoWindow) {
      const infoWindow = new google.maps.InfoWindow({
        content: InfoWindow,
      });
      marker.addListener('click', () => {
        infoWindow.open(ctx.map, marker);
      });
    }
    return marker;
  }

  clearRideHailingTracking(ctx: any): void {
    ctx._rideHailingHeadingToPickup = undefined;
    ctx._rideHailingRouteCache = undefined;
    ctx._rideHailingSuggestedRouteCache = undefined;
    ctx._rideHailingBearingState = undefined;

    if (ctx.driverMarker) {
      ctx.driverMarker.setMap(null);
      ctx.driverMarker = null;
    }

    if (ctx.pickupMarker) {
      ctx.pickupMarker.setMap(null);
      ctx.pickupMarker = null;
    }

    if (ctx.dropOffMarker) {
      ctx.dropOffMarker.setMap(null);
      ctx.dropOffMarker = null;
    }

    if (ctx.suggestedTripRoutePolyline) {
      this.clearRideHailingSuggestedRoute(ctx);
    }

    if (ctx.rideHailingLiveRoutePolyline) {
      ctx.rideHailingLiveRoutePolyline.setMap(null);
      ctx.rideHailingLiveRoutePolyline = null;
    }

    if (ctx.actualPathToPickupPolyline) {
      ctx.actualPathToPickupPolyline.setMap(null);
      ctx.actualPathToPickupPolyline = null;
    }
    if (ctx.actualPathToDropOffPolyline) {
      ctx.actualPathToDropOffPolyline.setMap(null);
      ctx.actualPathToDropOffPolyline = null;
    }
    ctx.actualPathToPickup = undefined;
    ctx.actualPathToDropOff = undefined;

    if (ctx.stageOnePolyMarkers) {
      ctx.stageOnePolyMarkers.forEach((marker: any) => marker.setMap(null));
      ctx.stageOnePolyMarkers = [];
    }

    if (ctx.stageTwoPolyMarkers) {
      ctx.stageTwoPolyMarkers.forEach((marker: any) => marker.setMap(null));
      ctx.stageTwoPolyMarkers = [];
    }

    if (ctx.stageOneTrackingPolyline) {
      ctx.stageOneTrackingPolyline.forEach((polyline: any) => polyline.setMap(null));

      ctx.stageOneTrackingPolyline = [];
    }

    if (ctx.stageTwoTrackingPolyline) {
      ctx.stageTwoTrackingPolyline.forEach((polyline: any) => polyline.setMap(null));

      ctx.stageTwoTrackingPolyline = [];
    }

    if (ctx.polyLines) {
      ctx.polyLines.forEach((polyline: any) => polyline.setMap(null));

      ctx.polyLines = [];
    }

    if (ctx.directionsRenderer) {
      ctx.directionsRenderer.setMap(null);
    }

    const orderStatus = ctx.bookingDetail?.orderStatus;

    if (orderStatus != null && rhEndedStatus.includes(orderStatus) && !ctx._rideHailingEndedHandled) {
      ctx._rideHailingEndedHandled = true;
      this.recenterMapToCurrentUserLocation(ctx);
    }
  }

  removeDriverLiveTracking(ctx: any): void {
  this.clearRideHailingTracking(ctx);
}

  private clearLiveRoutePolyline(ctx: any, cacheKey: string, polylineKey: string): void {
  this.clearConnector(ctx, 'liveTargetConnectorPolyline');
    ctx[cacheKey] = undefined;
    if (ctx[polylineKey]) {
      ctx[polylineKey].setMap(null);
      ctx[polylineKey] = null;
    }
  }

  handleRealTimeUpdationForLatAndLng(ctx: any): void {
    if (ctx._locationUpdateListenerBound) return;
    ctx._locationUpdateListenerBound = true;

    let stream = ctx._socketService.onEvent('updateLocationOk');
    if (ctx.destroy$ && typeof stream?.pipe === 'function') {
      stream = stream.pipe(takeUntil(ctx.destroy$));
    }

    stream.subscribe((res: any) => {
      const applyUpdate = () => {
  if (!res?.data || res.data.latitude == null || res.data.longitude == null || !ctx.bookingDetail) return;

  const normalizedDuration = res.data.trip_duration ?? res.data.distanceDetails ?? res.data.last_duration ?? null;
  if (normalizedDuration) {
    ctx.bookingDetail['last_duration'] = normalizedDuration;
    if (typeof ctx.updateTrackingEta === 'function') ctx.updateTrackingEta();
  }

  const orderStatus = Number(ctx.bookingDetail?.orderStatus);

  if (!this.isRideHailingLive(ctx)) {
    this.clearRideHailingTracking(ctx);
    return;
  }

  if (this.shouldShowRideHailingSuggestedRoute(orderStatus)) {
    this.clearLiveRoutePolyline(ctx, '_rideHailingRouteCache', 'rideHailingLiveRoutePolyline');
    this.updateRideHailingDriverMarker(ctx, false, 0, 0);
    this.refreshRideHailingMarkers(ctx);
    this.updateRideHailingSuggestedRoute(ctx);
    return;
  }

  this.clearRideHailingSuggestedRoute(ctx);

  const latitude = Number(res.data.latitude);
  const longitude = Number(res.data.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

  const headingToPickup = this.isRideHailingHeadingToPickup(ctx);
  this.refreshRideHailingStageMarkers(ctx, headingToPickup);

  this.applyDriverPosition(ctx, {
    headingToPickup,
    lat: latitude,
    lng: longitude,
    rawBearing: res.data.bearing,
    panMap: true,
  });
};

      if (ctx._zone && typeof ctx._zone.run === 'function') {
        ctx._zone.run(applyUpdate);
      } else {
        applyUpdate();
      }
    });
  }

  private shouldShowRideHailingSuggestedRoute(orderStatus: number | null | undefined): boolean {
    return [
      RhRideStatus.RIDE_BOOKED,
      RhRideStatus.DRIVER_ASSIGNED,
    ].includes(Number(orderStatus) as any);
  }

  private shouldTrackDriverToPickup(orderStatus: number | null | undefined): boolean {
    return [
      RhRideStatus.START_RIDE,
      RhRideStatus.NEAR_BY_PICKUP,
      RhRideStatus.ARRIVED_AT_PICKUP,
      RhRideStatus.WAITING
    ].includes(Number(orderStatus) as any);
  }

  private updateRideHailingSuggestedRoute(ctx: any): void {
    if (!ctx.map || !ctx.bookingDetail) return;

    const orderStatus = Number(ctx.bookingDetail.orderStatus);

    if (!this.shouldShowRideHailingSuggestedRoute(orderStatus)) {
      this.clearRideHailingSuggestedRoute(ctx);
      return;
    }

    const pickup = ctx.bookingDetail.pickup;
    const dropOff = ctx.bookingDetail.dropOf;

    const pickupLat = Number(pickup?.latitude);
    const pickupLng = Number(pickup?.longitude);
    const dropOffLat = Number(dropOff?.latitude);
    const dropOffLng = Number(dropOff?.longitude);

    if (
      !Number.isFinite(pickupLat) ||
      !Number.isFinite(pickupLng) ||
      !Number.isFinite(dropOffLat) ||
      !Number.isFinite(dropOffLng)
    ) {
      return;
    }

    const routeKey = 'pickupToDropOff';

    if (
      ctx._rideHailingSuggestedRouteCache?.leg === routeKey &&
      ctx._rideHailingSuggestedRouteCache?.coords?.length
    ) {
      if (ctx.suggestedTripRoutePolyline) {
        ctx.suggestedTripRoutePolyline.setMap(ctx.map);
      }

      return;
    }

    if (ctx._rideHailingSuggestedRouteCache?.fetching) {
      return;
    }

    if (!ctx.suggestedTripRoutePolyline) {
      ctx.suggestedTripRoutePolyline = new google.maps.Polyline({
        map: ctx.map,
        geodesic: true,
        strokeColor: '#000000',
        strokeOpacity: 1,
        strokeWeight: 3.5,
        zIndex: 4,
      });
    } else if (!ctx.suggestedTripRoutePolyline.getMap()) {
      ctx.suggestedTripRoutePolyline.setMap(ctx.map);
    }

    ctx._rideHailingSuggestedRouteCache = {
      leg: routeKey,
      coords: [
        { lat: pickupLat, lng: pickupLng },
        { lat: dropOffLat, lng: dropOffLng },
      ],
      fetching: true,
    };

    // Show an immediate fallback path while Directions API resolves.
    ctx.suggestedTripRoutePolyline.setPath([
      new google.maps.LatLng(pickupLat, pickupLng),
      new google.maps.LatLng(dropOffLat, dropOffLng),
    ]);

    if (!ctx.directionsService) {
      ctx.directionsService = new google.maps.DirectionsService();
    }

    ctx.directionsService.route(
      {
        origin: new google.maps.LatLng(pickupLat, pickupLng),
        destination: new google.maps.LatLng(dropOffLat, dropOffLng),
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (response: any, status: any) => {
        const currentStatus = Number(ctx.bookingDetail?.orderStatus);

        // The booking may have changed stage while the API request was running.
        if (!this.shouldShowRideHailingSuggestedRoute(currentStatus)) {
          this.clearRideHailingSuggestedRoute(ctx);
          return;
        }

        if (
          status !== google.maps.DirectionsStatus.OK ||
          !response?.routes?.length
        ) {
          if (ctx._rideHailingSuggestedRouteCache) {
            ctx._rideHailingSuggestedRouteCache.fetching = false;
          }

          return;
        }

        const overviewPath = response.routes[0].overview_path;

        const coords = overviewPath.map((point: any) => ({
          lat: point.lat(),
          lng: point.lng(),
        }));

        ctx._rideHailingSuggestedRouteCache = {
          leg: routeKey,
          coords,
          fetching: false,
        };

        if (ctx.suggestedTripRoutePolyline) {
          ctx.suggestedTripRoutePolyline.setPath(overviewPath);

          if (!ctx.suggestedTripRoutePolyline.getMap()) {
            ctx.suggestedTripRoutePolyline.setMap(ctx.map);
          }
          this.renderConnector(ctx, 'suggestedPickupConnectorPolyline',
            { lat: pickupLat, lng: pickupLng }, coords[0]);
          this.renderConnector(ctx, 'suggestedDropOffConnectorPolyline',
            { lat: dropOffLat, lng: dropOffLng }, coords[coords.length - 1]);
          }

        if (!ctx._initialTrackingViewportSet) {
          this.fitRouteBounds(ctx, coords);
        }
      },
    );
  }

  private isAutoFollowPaused(ctx: any): boolean {
    return Date.now() < (ctx._autoFollowPausedUntil || 0);
  }

  private disposeRouteOverlays(ctx: any): void {
    [
      'rideHailingLiveRoutePolyline',
      'chaufferLiveRoutePolyline',
      'suggestedTripRoutePolyline',
      'actualPathToPickupPolyline',
      'actualPathToDropOffPolyline',
      'suggestedPickupConnectorPolyline',
      'suggestedDropOffConnectorPolyline',
      'liveTargetConnectorPolyline',
    ].forEach((key) => {
      if (ctx[key]) {
        ctx[key].setMap(null);
        ctx[key] = null;
      }
    });
  
    if (ctx.directionsRenderer) {
      ctx.directionsRenderer.setMap(null);
      ctx.directionsRenderer = null;
    }
  }

  private clearRideHailingSuggestedRoute(ctx: any): void {
    ctx._rideHailingSuggestedRouteCache = undefined;
    this.clearConnector(ctx, 'suggestedPickupConnectorPolyline');
    this.clearConnector(ctx, 'suggestedDropOffConnectorPolyline');

    if (ctx.suggestedTripRoutePolyline) {
      ctx.suggestedTripRoutePolyline.setMap(null);
      ctx.suggestedTripRoutePolyline = null;
    }
  }
}